import {
  CreateRoadsideJobRequest,
  getRoadsideProductCodeToProductId,
  getRoadsideProductNameToProductId,
} from '@/interfaces/entities/jor-create-rsa-job.interface';
import { Address } from '@/interfaces';
import { PartyMatchStatus } from '@/interfaces/entities/roadside-assistance.interface';
import { JoinOnRoadDetails } from '@/interfaces/pages/join-on-road.interface';
import { AccountServices as _account } from '@/services/store/account.services';
import { useJoinOnRoadStore } from '@/store';
import { formatAddress } from '@/utils/address-format';

export const YOUR_DETAILS_STEP_ID = 'yourdetails';
export const YOUR_DETAILS_PERSONAL_DETAILS_STEP_ID = 'yourdetailspersonaldetails';
export const REVIEW_STEP_ID = 'review';
export const CONFIRM_PURCHASE_STEP_ID = 'confirmpurchase';
export const PAYMENT_STEP_ID = 'payment';
export const PAYMENT_CONFIRMATION_STEP_ID = 'paymentconfirmation';

export default {
  // Step 1 (Breakdown details)
  enterComments: 'enterComments',

  // Step 1 (Breakdown details) - Breakdown cause
  causeValidationError: 'causeValidationError',

  // Step 2 (Vehicle details)
  registrationNumber: 'registrationNumber',
  findCarBtn: 'findCarBtn',
  cantFindCarMessage: 'cantFindCarMessage',
  regoResultsStepContent: 'regoResultsStepContent',
  makeAndModelResultsStepContent: 'makeAndModelResultsStepContent',
  findCar: 'findCar',

  // Step 3 (Your Details) - Personal details
  titleName: 'titleName',
  birthDate: 'birthDate',
  emailAddress: 'emailAddress',

  // Step 3 (Your Details)
  phoneNumber: 'phoneNumber',
  homeAddress: 'homeAddress',
  checkPostalAddress: 'checkPostalAddress',
  postalAddress: 'postalAddress',

  // Step 3 (Your Details) - Phone number today
  phoneNumberToday: 'phoneNumberToday',
  phoneNumberTodayDescription: 'phoneNumberTodayDescription',

  // Step 5 (Confirm Purchase)
  confirmPurchaseStepSettings: 'confirmPurchaseStepSettings',

  // Payment
  paymentProgressTracker: 'paymentProgressTracker',

  // Review
  reviewSummary: 'reviewSummary',
} as const;

export async function createRsaJobJoinOnRoadRequest(
  joinOnRoadDetails: JoinOnRoadDetails,
  apttusId: string | null,
  policyNumber: string | null,
  assetId: string | null,
  loyaltyLevel: string
): Promise<CreateRoadsideJobRequest> {
  // When party match status is Exact, need to lookup the existing user to retrieve loyaltyType and address.
  // Otherwise treat the loyalty level as Blue and the address as entered by the user.
  let address = formatAddress(joinOnRoadDetails.homeAddress) || '';

  if (!address || address.trim().length === 0) {
    // No address supplied by user - retrieve from apttusId
    const residentialAddressResponse = await _account.getResidentialAddress(apttusId);
    if (residentialAddressResponse) {
      address = setResidentialAddress(residentialAddressResponse);
    }
  }

  const baseRequest = createBaseRsaJob(
    joinOnRoadDetails,
    'HTML JOR',
    assetId!,
    address ?? '',
    loyaltyLevel,
    getRoadsideProductCodeToProductId(joinOnRoadDetails.jorSelectedProduct!)!
  );

  return {
    ...baseRequest,
    policyNumber: policyNumber!,
  } as CreateRoadsideJobRequest;
}

export async function createRsaJobHtmlRequest(
  joinOnRoadDetails: JoinOnRoadDetails
): Promise<Partial<CreateRoadsideJobRequest>> {
  const joinOnRoadStore = useJoinOnRoadStore();

  const isExactMatch = joinOnRoadStore.partyMatchState?.matchStatus === PartyMatchStatus.Exact;
  const isLoggedIn = joinOnRoadStore.isMemberLoggedIn;

  let address = '';
  let apttusId = '';
  let loyaltyLevel: string | null = '';
  let membershipCardNumber: string | null = '';

  loyaltyLevel = joinOnRoadStore.eligibilityState?.membershipLoyaltyLevel ?? null;

  if (isExactMatch) {
    apttusId = joinOnRoadStore.partyMatchState!.apttusId!;
    membershipCardNumber = await _account.getMembershipCardNumber(joinOnRoadStore.apttusId);
  }

  if (isLoggedIn) {
    apttusId = joinOnRoadStore.apttusId!;
    membershipCardNumber = joinOnRoadStore.membershipCardNumber!;
  }

  const residentialAddressResponse = await _account.getResidentialAddress(apttusId);
  if (residentialAddressResponse) {
    address = setResidentialAddress(residentialAddressResponse) ?? '';
  }

  // If the member can create the RSA job by their loyalty level and not their policy then default to 'RACQ Everyday'
  const policyType = joinOnRoadStore.eligibilityState!.policyEligibleForAnyVehicles ?? 'RACQ Everyday';
  const roadsideProductId = getRoadsideProductNameToProductId(policyType);

  const baseRequest = createBaseRsaJob(
    joinOnRoadDetails,
    'HTML',
    membershipCardNumber!,
    address,
    loyaltyLevel!,
    roadsideProductId!
  );

  return {
    ...baseRequest,
  } as CreateRoadsideJobRequest;
}

function createBaseRsaJob(
  joinOnRoadDetails: JoinOnRoadDetails,
  channel: string,
  membershipCardNumber: string,
  address: string,
  loyaltyLevel: string,
  roadsideProductId: string
): Partial<CreateRoadsideJobRequest> {
  return {
    channel: channel,
    comments: [joinOnRoadDetails.comments],
    triageAnswers: {
      SAFE_LOCATION: ['NO'],
      BREAKDOWN_REASON: [joinOnRoadDetails.breakdownCause],
    },
    membership: {
      roadsideProductId: roadsideProductId,
      membershipCardNumber: membershipCardNumber,
      lastName: joinOnRoadDetails.lastName,
      firstName: joinOnRoadDetails.firstName,
      contact: {
        phone: joinOnRoadDetails.phoneNumberToday,
      },
      address: address,
      loyaltyLevel: loyaltyLevel,
    },
    vehicle: {
      colour: joinOnRoadDetails.vehicleColour,
      make: joinOnRoadDetails.vehicleMake,
      model: joinOnRoadDetails.vehicleModel,
      registration: joinOnRoadDetails.vehicleRegistration,
      buildYear: parseInt(joinOnRoadDetails.vehicleYear, 10),
      location: {
        latitude: joinOnRoadDetails.breakdownLocationLatitude,
        longitude: joinOnRoadDetails.breakdownLocationLongitude,
        comment: joinOnRoadDetails.breakdownLocationAddress,
      },
    },
  } as Partial<CreateRoadsideJobRequest>;
}

function setResidentialAddress(address: Address): string {
  return formatAddress({
    country: address.country,
    unitNumber: address.unit,
    streetNumber: address.streetNumber,
    street: address.street,
    suburb: address.suburb,
    state: address.state,
    postcode: address.postcode,
  });
}
